import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { BaseModalType } from '@context/ModalsContext'

import { Body2, Title2 } from '@ui/Typography'
import { Input } from '@ui/components/Input'
import { Textarea } from '@ui/components/Input/Textarea/Textarea'
import { ListItem } from '@ui/components/List'
import { Modal } from '@ui/components/Modal'
import { MetricsIcon } from '@ui/icons/Metrics.icon'
import { SquaresIcon } from '@ui/icons/Squares.icon'

import { Box } from '@shared/components/Box'

import type { DashboardLayout } from '../types'
import { DESCRIPTION_LIMIT, LAYOUT_NAME_REGEX, TITLE_LIMIT } from '../types'

interface CreateLayoutModalProps {
  onCreate: (name: string, description: string, presetId?: string, isDefault?: boolean) => void
  presetLayouts: DashboardLayout[]
}

export const CreateLayoutModal: BaseModalType<CreateLayoutModalProps> = ({
  onCreate,
  presetLayouts,
  close,
}) => {
  const { t } = useTranslation()

  const [layoutName, setLayoutName] = useState('')
  const [layoutDescription, setLayoutDescription] = useState('')
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const [nameError, setNameError] = useState('')
  const [descriptionError, setDescriptionError] = useState('')
  const [isDefault, setIsDefault] = useState(false)

  const handleCreate = () => {
    if (!layoutName.trim()) {
      setNameError(t('Dashboard.LayoutNameRequired'))
      return
    }

    if (layoutName.length > TITLE_LIMIT) {
      setNameError(t('Dashboard.LayoutTitleMaxLength', { count: TITLE_LIMIT }))
      return
    }

    if (layoutDescription.length > DESCRIPTION_LIMIT) {
      setDescriptionError(t('Dashboard.LayoutDescriptionMaxLength', { limit: DESCRIPTION_LIMIT }))
      return
    }

    if (!LAYOUT_NAME_REGEX.test(layoutName)) {
      setNameError(t('Dashboard.LayoutNameInvalid'))
      return
    }

    onCreate(layoutName, layoutDescription, selectedPreset || undefined, isDefault)
    setLayoutName('')
    setLayoutDescription('')
    setSelectedPreset(null)
    setIsDefault(false)
    setNameError('')
    setDescriptionError('')
    close()
  }

  const handleClose = () => {
    setLayoutName('')
    setLayoutDescription('')
    setSelectedPreset(null)
    setIsDefault(false)
    setNameError('')
    setDescriptionError('')
    close()
  }

  return (
    <Modal>
      <Modal.Header canClose>{t('Dashboard.CreateNewLayout')}</Modal.Header>

      <Modal.Body>
        <Box fullWidth fullHeight>
          <Box mb='x16'>
            <Input
              label={t('Dashboard.LayoutNameLabel')}
              value={layoutName}
              onChange={(e) => {
                setLayoutName(e.target.value)
                setNameError('')
              }}
              maxLength={TITLE_LIMIT}
              fullWidth
              error={Boolean(nameError)}
              hint={nameError}
            />
            <Body2 color='text-med-em'>
              {layoutName.length}/{TITLE_LIMIT}
            </Body2>
          </Box>

          <Box mb='x16'>
            <Textarea
              label={t('Dashboard.LayoutDescriptionLabel')}
              value={layoutDescription}
              onChange={(e) => {
                setLayoutDescription(e.target.value)
                setDescriptionError('')
              }}
              maxLength={DESCRIPTION_LIMIT}
              rows={2}
              dynamicHeight
              fullWidth
              maxHeight='150px'
              error={Boolean(descriptionError)}
              hint={descriptionError}
            />
            <Body2 color='text-med-em'>
              {layoutDescription.length}/{DESCRIPTION_LIMIT}
            </Body2>
          </Box>
          <Box>
            <Box flex direction='column' gap='x12' mt='x8' maxHeight='20rem' overflowY='auto'>
              <ListItem
                padding='vertical'
                active={isDefault}
                onClick={() => setIsDefault((prev) => !prev)}
                endSlot={{
                  toggle: {
                    name: 'isDefault',
                    value: isDefault,
                    onChange: setIsDefault,
                  },
                }}
              >
                {t('Dashboard.Make this layout default')}
              </ListItem>

              <Title2 color='text-med-em' medium>
                {t('Dashboard.StartFromTemplate')}
              </Title2>

              <ListItem
                subtitle={t('Dashboard.BlankLayoutDescription')}
                active={selectedPreset === null}
                onClick={() => setSelectedPreset(null)}
                startSlot={{
                  icon: { icon: SquaresIcon, color: 'text-white' },
                  size: 'x40',
                  shape: 'squircle',
                  bg: 'surface-primary-high-em',
                }}
                endSlot={{
                  radio: {
                    checked: selectedPreset === null,
                    disabled: false,
                    size: 'md',
                  },
                }}
              >
                {t('Dashboard.BlankLayout')}
              </ListItem>

              {presetLayouts.map((preset) => (
                <ListItem
                  key={preset.id}
                  subtitle={`${preset.description} (${t('Dashboard.WidgetsCount', {
                    count: preset.widgets.length,
                  })})`}
                  active={selectedPreset === preset.id}
                  onClick={() => setSelectedPreset(preset.id)}
                  startSlot={{
                    icon: { icon: MetricsIcon, color: 'text-white' },
                    size: 'x40',
                    shape: 'squircle',
                    bg: 'surface-primary-high-em',
                  }}
                  endSlot={{
                    radio: {
                      checked: selectedPreset === preset.id,
                      disabled: false,
                      size: 'md',
                    },
                  }}
                >
                  {preset.name}
                </ListItem>
              ))}
            </Box>
          </Box>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Secondary onClick={handleClose}>
          {t('Dashboard.Cancel')}
        </Modal.Footer.Secondary>

        <Modal.Footer.Primary onClick={handleCreate}>
          {t('Dashboard.CreateLayout')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
